import React, { useEffect, useMemo, useState } from "react";
import ModalCloseBtn from "@/components/ModalCloseBtn";
import { Users, FileSignature, AlertTriangle, RefreshCcw, Send, Eye, X, History as HistoryIcon, Download, IdCard, Mail, Calendar, MapPin } from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth, ROLE_LABELS, ROLE_COLORS } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";
import LOABuilderModal from "@/pages/shared/LOABuilderModal";
import RoleBadge from "@/components/RoleBadge";

export default function TeamPage() {
  const { user, customRole } = useAuth();
  const [team, setTeam] = useState([]);
  const [loas, setLoas] = useState([]);
  const [notif, setNotif] = useState({ items: [], count: 0 });
  const [openLoa, setOpenLoa] = useState(null); // { mode: 'issue'|'renew', target, existing }
  const [viewProfile, setViewProfile] = useState(null);
  const [customRoles, setCustomRoles] = useState([]);

  const load = async () => {
    const [t, l, n, cr] = await Promise.all([
      api.get("/users/my-team"),
      api.get("/loas", { params: { issued_by: user.user_id } }),
      api.get("/loas/notifications/me"),
      api.get("/admin/custom-roles").catch(() => ({ data: [] })),
    ]);
    setTeam(t.data || []);
    setLoas(l.data || []);
    setNotif(n.data || { items: [], count: 0 });
    setCustomRoles(cr.data || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const loasByUser = useMemo(() => {
    const m = {};
    for (const l of loas) {
      if (!m[l.issued_to]) m[l.issued_to] = [];
      m[l.issued_to].push(l);
    }
    return m;
  }, [loas]);

  const latestLoa = (uid) => {
    const list = loasByUser[uid] || [];
    return list[0]; // already sorted desc by issued_at server-side
  };

  const notifMap = useMemo(() => {
    const m = {};
    for (const n of notif.items) m[n.issued_to] = n;
    return m;
  }, [notif]);

  return (
    <Layout navItems={getNav(user.role, customRole)}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="team-page">
        <PageHeader
          title="My Team"
          subtitle="Members who report to you. Issue and renew LOAs from here."
          icon={Users}
          accent="teal"
          actions={
            <div className="flex items-center gap-2">
              {notif.count > 0 && (
                <div className="px-3 py-2 rounded-xl bg-rose-50 text-rose-700 text-xs flex items-center gap-2 font-semibold" data-testid="loa-notif-banner">
                  <AlertTriangle className="w-4 h-4" /> {notif.count} LOA{notif.count > 1 ? "s" : ""} need attention
                </div>
              )}
            </div>
          }
        />

        {team.length === 0 ? (
          <div className="dashboard-card p-12 text-center text-slate-400">
            No team members assigned yet. Ask an admin to wire reporting parents in the Org Chart.
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-5">
            {team.map((m) => {
              const last = latestLoa(m.user_id);
              const n = notifMap[m.user_id];
              const status = n ? (n.expired ? "expired" : `expires in ${n.days_left}d`) : null;
              return (
                <div key={m.user_id} className={`dashboard-card p-5 relative ${n ? "ring-2 ring-rose-300" : ""}`} data-testid={`team-member-${m.user_id}`}>
                  {n && (
                    <div className={`absolute -top-2 right-4 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${n.expired ? "bg-rose-500" : "bg-amber-500"} text-white uppercase`} data-testid={`loa-alert-${m.user_id}`}>
                      <AlertTriangle className="w-3 h-3 inline mr-1" />{status}
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-400 flex items-center justify-center text-white font-bold text-lg overflow-hidden">
                      {m.picture ? <img src={m.picture} alt="" className="w-full h-full object-cover" /> : m.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate">{m.name}</div>
                      <div className="text-xs text-slate-500 truncate flex items-center gap-1"><Mail className="w-3 h-3" />{m.email}</div>
                    </div>
                  </div>
                  <div className="space-y-1.5 text-xs mb-4">
                    <Pair label="Role"><RoleBadge role={m.role} customRoles={customRoles} /></Pair>
                    <Pair label="Member ID"><span data-testid={`member-id-${m.user_id}`}>{m.member_id || "—"}</span></Pair>
                    {(m.city || m.region) && <Pair label="Location"><MapPin className="w-3 h-3 inline" /> {m.city || m.region}</Pair>}
                    {m.end_tenure_date && <Pair label="End tenure"><b className={n?.expired ? "text-rose-700" : "text-slate-700"}>{m.end_tenure_date}</b></Pair>}
                    {last && <Pair label="Last LOA"><span className="text-slate-600">{new Date(last.issued_at).toLocaleDateString()}</span></Pair>}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => setViewProfile(m)} className="btn-ghost text-xs py-1.5 flex-1" data-testid={`view-profile-${m.user_id}`}><Eye className="w-3 h-3" /> Profile</button>
                    {!last ? (
                      <button onClick={() => setOpenLoa({ mode: "issue", target: m })} className="btn-primary text-xs py-1.5 flex-1" data-testid={`issue-loa-${m.user_id}`}><FileSignature className="w-3 h-3" /> Issue LOA</button>
                    ) : (
                      <button onClick={() => setOpenLoa({ mode: "renew", target: m, existing: last })} className={`text-xs py-1.5 flex-1 ${n ? "btn-primary" : "btn-ghost"}`} data-testid={`renew-loa-${m.user_id}`}>
                        <RefreshCcw className="w-3 h-3" /> Renew
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {openLoa && (
        <LOABuilderModal
          mode={openLoa.mode}
          target={openLoa.target}
          existing={openLoa.existing}
          onClose={() => setOpenLoa(null)}
          onSuccess={() => { setOpenLoa(null); load(); }}
        />
      )}

      {viewProfile && (
        <ProfileSheet member={viewProfile} loas={loasByUser[viewProfile.user_id] || []} customRoles={customRoles} onClose={() => setViewProfile(null)} />
      )}
    </Layout>
  );
}

function Pair({ label, children }) {
  return (
    <div className="flex items-center justify-between text-[11px] text-slate-500">
      <span className="uppercase tracking-wide font-semibold">{label}</span>
      <span>{children}</span>
    </div>
  );
}

function ProfileSheet({ member, loas, customRoles = [], onClose }) {
  const downloadLoa = (lid) => {
    const token = localStorage.getItem("munners_token");
    window.open(`${BACKEND_URL}/api/loas/${lid}/pdf?t=${token}`, "_blank");
  };
  const resend = async (lid) => {
    try {
      const r = await api.post(`/loas/${lid}/resend`, {});
      if (r.data.sent) toast.success("Email resent");
      else toast.warning(r.data.message || "Email pending (SMTP not set)");
    } catch (e) { toast.error("Failed"); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative" data-testid="profile-sheet">
        <div className="p-6 border-b border-slate-100 flex items-start gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-400 flex items-center justify-center text-white font-display font-black text-2xl overflow-hidden flex-shrink-0">
            {member.picture ? <img src={member.picture} alt="" className="w-full h-full object-cover" /> : member.name?.charAt(0)?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-black text-xl truncate">{member.name}</h3>
            <div className="text-xs text-slate-500 truncate">{member.email}</div>
            <div className="mt-1"><RoleBadge role={member.role} customRoles={customRoles} /></div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 grid md:grid-cols-2 gap-4 text-sm">
          <Info Icon={IdCard} label="Member ID" value={member.member_id || "—"} />
          <Info Icon={Calendar} label="End of Tenure" value={member.end_tenure_date || "—"} />
          <Info Icon={MapPin} label="City / Region" value={member.city || member.region || "—"} />
          <Info Icon={Mail} label="Contact" value={member.contact_number || "—"} />
          {member.address && <Info Icon={MapPin} label="Address" value={member.address} full />}
          {member.goals && <Info Icon={null} label="Goals" value={member.goals} full />}
          {member.bio && <Info Icon={null} label="Bio" value={member.bio} full />}
        </div>

        {loas.length > 0 && (
          <div className="px-6 pb-6">
            <h4 className="font-display font-bold flex items-center gap-2 mb-3"><HistoryIcon className="w-4 h-4 text-emerald-500" /> LOA history ({loas.length})</h4>
            <div className="space-y-2">
              {loas.map((l) => (
                <div key={l.loa_id} className="p-3 rounded-xl bg-slate-50 border-l-4 border-emerald-400" data-testid={`history-loa-${l.loa_id}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{l.title}</div>
                      <div className="text-xs text-slate-500">Issued {new Date(l.issued_at).toLocaleDateString()} · {l.tenure_start || "—"} → <b className="text-emerald-700">{l.tenure_end}</b></div>
                      {l.last_renewed_at && <div className="text-[10px] text-amber-700 mt-0.5"><RefreshCcw className="w-2.5 h-2.5 inline" /> renewed {new Date(l.last_renewed_at).toLocaleDateString()}</div>}
                    </div>
                    <button onClick={() => downloadLoa(l.loa_id)} className="text-xs btn-ghost py-1 px-2"><Download className="w-3 h-3" /></button>
                    <button onClick={() => resend(l.loa_id)} className="text-xs btn-ghost py-1 px-2" data-testid={`resend-${l.loa_id}`}><Send className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ Icon, label, value, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wide font-bold text-slate-400 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="text-slate-800 font-semibold mt-0.5 break-words">{value}</div>
    </div>
  );
}
